import _ from "underscore";
import { isInCanada } from "hos-tools";
import type AppView from "../../app.view";
import type Translator from "../../utils/translator";
import { createElement } from "../../utils/vanilla";
import { Dialog, offsetArrowUp } from "../framework/dialog";
import { generateChecklist, Item } from "../framework/checklist";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import { MobileActions } from "../../store/mobile/actions";
import { getRoadsideCheckLanguageName, getRoadsideCheckLanguageOptions } from "../../utils/languageOptions";
import moduleTranslations from "../../modules/module-translations";
import selectorHTML from "./roadsideCheckLanguageSelector.tmpl";

const selectorTemplate = _.template(selectorHTML);

/**
 * Template variables for the Roadside Check language selector. Every key is always defined so
 * host templates (and the shared dvir/shared/main.tmpl) never hit a lodash-template
 * `ReferenceError` for a missing variable, regardless of whether the selector is shown.
 */
export interface IRoadsideCheckLanguageViewModel {
    showLanguageSelector: boolean;
    currentLanguageName: string;
    /** Pre-rendered button markup (roadsideCheckLanguageSelector.tmpl); interpolate with `<%= languageSelectorHtml %>`. */
    languageSelectorHtml: string;
}

export interface IRoadsideCheckLanguageSelectorOptions {
    app: AppView;
    /** The host view's translator (DriveView.translator is a getter, so this is resolved lazily). */
    getTranslator: () => Translator;
    /** The host view's root element; used to find the selector button for aria/outside-click handling. */
    getElement: () => HTMLElement | undefined;
    /** Host-supplied so callers can go through their own hosUtils reference (report.ts caches `this.hosUtils`). */
    getHosRuleSetIdShortened: () => string;
    /** Runs after the language has been applied and `UserUpdateLanguage` fired. Each host re-renders differently. */
    onLanguageApplied: (language: string) => void;
}

function buildViewModel(app: AppView, showLanguageSelector: boolean): IRoadsideCheckLanguageViewModel {
    const currentLanguage = app.translationService.getLanguage() || "en";
    const currentLanguageName = getRoadsideCheckLanguageName(currentLanguage);
    const templateVariables = {
        showLanguageSelector,
        currentLanguageName
    };
    return {
        ...templateVariables,
        languageSelectorHtml: selectorTemplate(templateVariables)
    };
}

/**
 * View model for pages that share a Roadside Check template but never show the selector
 * (e.g. dvir/main.ts renders dvir/shared/main.tmpl). Returns the full object so the shared
 * template can be rendered without special-casing any variable.
 */
export function getHiddenRoadsideCheckLanguageViewModel(app: AppView): IRoadsideCheckLanguageViewModel {
    return buildViewModel(app, false);
}

/**
 * The Roadside Check language selector. Owned by a host view via composition:
 * the host wires `"click #roadside-check-language-selector"` to `handleClick()`, spreads
 * `getViewModel()` into its template variables, and calls `remove()` from its own `remove()`.
 */
export class RoadsideCheckLanguageSelector {
    private readonly app: AppView;
    private languageDialog?: Dialog;
    private removed = false;
    private languageMenuOpen = false;

    constructor(private readonly options: IRoadsideCheckLanguageSelectorOptions) {
        this.app = options.app;
    }

    /**
     * @param canShow extra host guard AND-ed into `showLanguageSelector` (e.g. dvir/view.ts only shows
     * the selector in inspection mode). Short-circuits so the ruleset lookup is skipped when false.
     */
    getViewModel(canShow = true): IRoadsideCheckLanguageViewModel {
        const driverLanguage = this.app.getUserLanguage();
        const showLanguageSelector = canShow && this.shouldShow(driverLanguage);
        return buildViewModel(this.app, showLanguageSelector);
    }

    handleClick() {
        if (this.languageMenuOpen) {
            this.languageDialog?.remove();
            return;
        }
        this.openLanguageMenu();
    }

    remove() {
        this.removed = true;
        this.languageDialog?.remove();
        this.languageDialog = undefined;
    }

    private shouldShow(driverLanguage: string) {
        return getRoadsideCheckLanguageOptions(
            driverLanguage,
            isInCanada(this.options.getHosRuleSetIdShortened()),
            getRoadsideCheckLanguageName
        ).length > 1;
    }

    private openLanguageMenu() {
        const currentLanguage = this.app.translationService.getLanguage() || "en";
        const driverLanguage = this.app.getUserLanguage();
        const isCanada = isInCanada(this.options.getHosRuleSetIdShortened());
        const options = getRoadsideCheckLanguageOptions(driverLanguage, isCanada, getRoadsideCheckLanguageName);
        const dialogId = "language-selection-dialog";
        const selector = "#roadside-check-language-selector";
        const listContainer = createElement("div", undefined, "languageSelection");
        const items: Item[] = options
            .sort((a, b) => (b.id === currentLanguage ? 1 : 0) - (a.id === currentLanguage ? 1 : 0))
            .map(option => new Item({
                id: option.id,
                title: option.children,
                selected: option.id === currentLanguage
            }));

        let languageDialog: Dialog;
        const languageChecklist = generateChecklist({
            items,
            isCheckable: true,
            labelledBy: `${ dialogId }-title`,
            selectCallback: item => {
                const value = item.get("id");
                languageChecklist.remove();
                languageDialog.remove();
                if (value) {
                    this.applyLanguage(value);
                }
            },
            context: this,
            translator: this.options.getTranslator(),
            noTranslate: true,
            el: listContainer
        });

        const html = createElement("div").appendChild(listContainer);
        languageDialog = new Dialog(
            dialogId,
            [html],
            this.options.getTranslator(),
            "Change language",
            undefined,
            {
                modal: false,
                showCloseIcon: false,
                alignToElement: selector,
                isNightMode: this.app.store.getState().data.nightMode,
                customClass: "language-selection-popover",
                hideTitle: true
            }
        );

        const onOutsideClick = (event: Event) => {
            const target = event.target as Node;
            const dialogEl = document.getElementById(dialogId);
            const languageButton = this.options.getElement()?.querySelector<HTMLElement>(selector);
            if (dialogEl && !dialogEl.contains(target) && !languageButton?.contains(target)) {
                languageDialog.remove();
            }
        };

        const languageButton = this.options.getElement()?.querySelector<HTMLElement>(selector);
        languageButton?.setAttribute("aria-expanded", "true");

        languageDialog.onRemove(() => {
            document.removeEventListener("click", onOutsideClick, true);
            this.languageDialog = undefined;
            if (!this.languageMenuOpen) {
                return;
            }
            this.languageMenuOpen = false;
            const button = this.options.getElement()?.querySelector<HTMLElement>(selector);
            button?.setAttribute("aria-expanded", "false");
        });

        void languageDialog.open().then(() => {
            if (this.removed) {
                languageDialog.remove();
                return;
            }
            this.languageDialog = languageDialog;
            this.languageMenuOpen = true;
            languageButton && offsetArrowUp(languageButton, dialogId);
            requestAnimationFrame(() => setTimeout(() => document.addEventListener("click", onOutsideClick, true), 0));
        });
    }

    private applyLanguage(language: string) {
        this.app.translationService.setLanguage(language);
        this.app.store.dispatch(MobileActions.setLanguage(language));
        void this.app.translationService.getTranslator().translate(moduleTranslations)
            .catch(() => { /* fall back to whatever is cached */ })
            .then(() => {
                // Deliberately payload-free: a user payload makes AppView.onUserLanguageUpdate run a
                // destructive app-wide re-render that discards the current DVIR sub-page.
                dispatcher.trigger(DispatcherEvents.UserUpdateLanguage);
                if (!this.removed) {
                    this.options.onLanguageApplied(language);
                }
            });
    }
}
